import { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router';
import {
  DataGrid,
  type GridFilterModel,
  type GridPaginationModel,
  type GridColDef,
  type GridRenderCellParams,
  type GridRowSelectionModel,
} from '@mui/x-data-grid';
import {
  Box,
  Typography,
  Button,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link,
  Alert,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { Link as RouterLink } from 'react-router';
import { useApi } from '@/providers/ApiContext';
import { getDocuments, deleteDocument } from '@/actions/document';
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarDensitySelector,
  GridToolbarExport,
  GridToolbarQuickFilter,
} from '@mui/x-data-grid';
import AddCircleOutlineOutlinedIcon from '@mui/icons-material/AddCircleOutlineOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import ErrorSnackBar from '@/components/shared/ErrorSnackBar';
import type { Document, DocumentType, EntityId } from '@/types';
import type { GridRowId } from '@mui/x-data-grid';

interface DocumentGridToolbarProps {
  onAddClick: () => void;
  onDeleteClick: () => void;
  selectedRows: GridRowId[];
}

function DocumentGridToolbar({
  onAddClick,
  onDeleteClick,
  selectedRows,
}: DocumentGridToolbarProps): React.JSX.Element {
  return (
    <GridToolbarContainer
      sx={{
        '& .MuiButtonBase-root': {
          color: 'black',
        },
      }}
    >
      <Button
        sx={{
          padding: '3px 9px',
          '& .MuiButtonBase-root svg': {
            color: 'primary',
          },
        }}
        variant='outlined'
        onClick={onAddClick}
        color='primary'
      >
        <Stack direction='row' spacing={1} alignItems='center' sx={{}}>
          <AddCircleOutlineOutlinedIcon color='primary' />
          <Typography
            color='primary'
            variant='button'
            display='block'
            gutterBottom
          >
            Add
          </Typography>
        </Stack>
      </Button>

      <Button
        sx={{ padding: '3px 9px' }}
        onClick={onDeleteClick}
        disabled={!selectedRows?.length}
      >
        <Stack direction='row' spacing={1} alignItems='center'>
          <DeleteOutlinedIcon />
          <Typography variant='button' display='block' gutterBottom>
            Delete
          </Typography>
        </Stack>
      </Button>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarDensitySelector />
      <GridToolbarExport />
      <Box sx={{ flexGrow: 1 }} />
      <GridToolbarQuickFilter
        debounceMs={500}
        placeholder='Search documents...'
      />
    </GridToolbarContainer>
  );
}

interface AdminDocumentListProps {
  doc_type: DocumentType;
}

function AdminDocumentList({
  doc_type,
}: AdminDocumentListProps): React.JSX.Element {
  const navigate = useNavigate();
  const api = useApi();
  const [documents, setDocuments] = useState<Document[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>({
      type: 'include',
      ids: new Set<GridRowId>(),
    });
  const [api_error, setApiError] = useState<string | null>(null);
  const [selectedRows, setSelectedRows] = useState<GridRowId[]>([]);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [pageSize, setPageSize] = useState<number>(50);
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [cursors, setCursors] = useState<Record<number, string>>({});
  const [searchFilter, setSearchFilter] = useState<string>('');
  const [debouncedSearchFilter, setDebouncedSearchFilter] =
    useState<string>('');
  const [columnFilters, setColumnFilters] = useState<Record<string, unknown>>(
    {},
  );
  const [debouncedColumnFilters, setDebouncedColumnFilters] = useState<
    Record<string, unknown>
  >({});
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
    quickFilterValues: [],
  });
  const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const columnFilterTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Debounce search filter changes - only trigger fetch, don't reset state
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearchFilter(searchFilter);
    }, 500); // Longer delay to prevent excessive API calls

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchFilter]);

  // Debounce column filter changes - only trigger fetch, don't reset state
  useEffect(() => {
    if (columnFilterTimeoutRef.current) {
      clearTimeout(columnFilterTimeoutRef.current);
    }

    columnFilterTimeoutRef.current = setTimeout(() => {
      setDebouncedColumnFilters(columnFilters);
    }, 200); // Shorter delay for dropdown selections

    return () => {
      if (columnFilterTimeoutRef.current) {
        clearTimeout(columnFilterTimeoutRef.current);
      }
    };
  }, [columnFilters]);

  const fetchDocuments = useCallback(
    async (
      currentPage: number,
      startKey: string | null = null,
    ): Promise<void> => {
      if (!api) return;
      try {
        setLoading(true);
        console.log('Fetching documents with:', { pageSize, startKey });
        const response = await getDocuments(
          api,
          doc_type.entity_id,
          pageSize,
          startKey || undefined,
          debouncedSearchFilter || undefined,
        );
        console.log('Received response:', response);
        setDocuments(response.results);
        setHasNextPage(response.last_key !== null);
        if (response.last_key) {
          setCursors((prev) => ({
            ...prev,
            [currentPage + 1]: response.last_key as string,
          }));
        }
        setApiError(null);
      } catch (err) {
        const error = err as Error;
        setApiError(
          error.message ||
            'Unable to fetch documents. This could be due to network issues or server problems.',
        );
        console.error('Error fetching documents:', err);
      } finally {
        setLoading(false);
      }
    },
    [api, doc_type.entity_id, pageSize, debouncedSearchFilter],
  );

  useEffect(() => {
    if (doc_type.entity_id) {
      setCurrentPage(0);
      setCursors({});
      fetchDocuments(0);
    }
  }, [doc_type.entity_id, fetchDocuments]);

  // Reset pagination when debounced search filter or column filters change
  useEffect(() => {
    if (doc_type.entity_id) {
      setCurrentPage(0);
      setCursors({});
      // Don't reset documents to null - just fetch new data
      fetchDocuments(0);
    }
  }, [
    debouncedSearchFilter,
    debouncedColumnFilters,
    doc_type.entity_id,
    fetchDocuments,
  ]);

  const handleFilterModelChange = useCallback(
    (model: GridFilterModel): void => {
      console.log('Filter model changed:', model);

      // Update the filter model state to maintain it
      setFilterModel(model);

      // Handle QuickFilter - only update if it's actually different
      const newSearchFilter = model.quickFilterValues?.join(' ') || '';
      if (newSearchFilter !== searchFilter) {
        console.log(
          'Search filter changed from:',
          searchFilter,
          'to:',
          newSearchFilter,
        );
        setSearchFilter(newSearchFilter);
      }

      // Handle column filters
      const newColumnFilters: Record<string, unknown> = {};
      if (model.items) {
        model.items.forEach((item) => {
          if (
            item.field &&
            item.value !== undefined &&
            item.value !== null &&
            item.value !== ''
          ) {
            newColumnFilters[item.field] = item.value;
          }
        });
      }

      console.log('New column filters:', newColumnFilters);

      // Only update if column filters have changed
      const columnFiltersChanged =
        JSON.stringify(newColumnFilters) !== JSON.stringify(columnFilters);
      if (columnFiltersChanged) {
        setColumnFilters(newColumnFilters);
      }
    },
    [searchFilter, columnFilters],
  );

  const handleAddClick = (): void => {
    navigate(`/admin/documents/edit?type=${doc_type.code}`);
  };

  const handleDeleteClick = (): void => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async (): Promise<void> => {
    if (!api) return;
    try {
      setLoading(true);
      await Promise.all(
        selectedRows.map((id) => deleteDocument(api, id as EntityId)),
      );
      setCurrentPage(0);
      setCursors({});
      await fetchDocuments(0);
      setSelectedRows([]);
    } catch (err) {
      const error = err as Error;
      setApiError(
        error.message ||
          'Unable to delete documents. This could be due to network issues, server problems, or insufficient permissions.',
      );
      console.error('Error deleting documents:', err);
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
    }
  };

  const handleDeleteCancel = (): void => {
    setDeleteDialogOpen(false);
  };

  const handleRowSelection = useCallback(
    (value: GridRowSelectionModel): void => {
      setRowSelectionModel(value);
      setSelectedRows(Array.from(value.ids));
    },
    [setSelectedRows],
  );

  const handlePaginationModelChange = (model: GridPaginationModel): void => {
    console.log(
      'Pagination model change:',
      model,
      currentPage,
      hasNextPage,
      cursors,
    );

    if (model.page > currentPage && hasNextPage) {
      setCurrentPage(model.page);
      const nextCursor = cursors[model.page];
      console.log('Fetching next page with cursor:', nextCursor);
      fetchDocuments(model.page, nextCursor || null);
    } else if (model.page < currentPage) {
      setCurrentPage(model.page);
      if (model.page === 0) {
        console.log('Resetting to first page');
        fetchDocuments(0);
      } else {
        const prevCursor = cursors[model.page];
        console.log('Fetching previous page with cursor:', prevCursor);
        fetchDocuments(model.page, prevCursor || null);
      }
    }

    if (model.pageSize !== pageSize) {
      console.log('Page size change:', model.pageSize);
      setPageSize(model.pageSize);
      setCurrentPage(0);
      setCursors({});
      fetchDocuments(0);
    }
  };

  const renderName = useCallback(
    (params: GridRenderCellParams<Document>): React.JSX.Element => {
      return (
        <Link component={RouterLink} to={`/admin/documents/${params.id}`}>
          {params.row.name}
        </Link>
      );
    },
    [],
  );

  const convertDate = useCallback((value: string): Date | null => {
    return value ? new Date(value.replace(' ', 'T') + 'Z') : null;
  }, []);

  const columns: GridColDef<Document>[] = [
    { field: 'entity_id', headerName: 'id', type: 'string' as const, flex: 1 },
    {
      field: 'name',
      headerName: 'Name',
      type: 'string' as const,
      flex: 3,
      renderCell: renderName,
    },
    {
      field: 'doc_id',
      headerName: 'Document ID',
      type: 'string' as const,
      flex: 2,
    },
    { field: 'status', headerName: 'Status', type: 'string' as const, flex: 1 },
    {
      field: 'created_at',
      headerName: 'Created',
      type: 'dateTime' as const,
      flex: 2,
      valueGetter: (_value: unknown, row: Document) =>
        convertDate(row.created_at),
    },
    {
      field: 'updated_at',
      headerName: 'Last Changed',
      type: 'dateTime' as const,
      flex: 2,
      valueGetter: (_value: unknown, row: Document) =>
        convertDate(row.updated_at),
    },
  ];

  return (
    <>
      <Box
        sx={{
          height: 'calc(100vh - 200px)',
          width: '100%',
        }}
      >
        <DataGrid
          rows={documents}
          columns={columns}
          getRowId={(row) => row.entity_id}
          checkboxSelection
          disableRowSelectionOnClick
          sx={(theme) => ({
            // '& .MuiDataGrid-columnHeaders': {
            //   backgroundColor: theme.palette.grey[100],
            //   position: 'sticky',
            //   top: 0,
            //   zIndex: 100,
            // },
            '& .MuiDataGrid-columnHeader': {
              backgroundColor: theme.palette.grey[100],
            },
          })}
          loading={loading}
          paginationMode='server'
          filterMode='server'
          filterModel={filterModel}
          rowCount={
            hasNextPage ? -1 : currentPage * pageSize + documents.length
          }
          pageSizeOptions={[50, 100]}
          paginationModel={{
            pageSize,
            page: currentPage,
          }}
          onPaginationModelChange={handlePaginationModelChange}
          onFilterModelChange={handleFilterModelChange}
          showToolbar
          slots={{
            toolbar: () => (
              <DocumentGridToolbar
                onAddClick={handleAddClick}
                onDeleteClick={handleDeleteClick}
                selectedRows={selectedRows}
              />
            ),
          }}
          onRowSelectionModelChange={handleRowSelection}
          rowSelectionModel={rowSelectionModel}
          initialState={{
            columns: {
              columnVisibilityModel: {
                entity_id: false,
              },
            },
          }}
        />
      </Box>

      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby='delete-dialog-title'
        aria-describedby='delete-dialog-description'
        PaperProps={{
          sx: { maxHeight: '90vh', display: 'flex', flexDirection: 'column' },
        }}
      >
        <DialogTitle id='delete-dialog-title' sx={{ flexShrink: 0 }}>
          Confirm Delete
        </DialogTitle>
        <DialogContent
          id='delete-dialog-description'
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: 'auto',
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <Alert severity='error'>
            This action is permanent and cannot be reversed. The selected
            document(s) will be permanently removed and cannot be recovered.
            Are you sure you want to continue?
          </Alert>
          <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            <Typography variant='subtitle2' color='text.secondary'>
              Selected ({selectedRows.length}):
            </Typography>
            <List
              dense
              disablePadding
              sx={{
                maxHeight: 'min(40vh, 200px)',
                overflow: 'auto',
                border: 1,
                borderColor: 'divider',
                borderRadius: 1,
              }}
            >
              {selectedRows.map((id) => {
                const doc = documents.find((d) => d.entity_id === id);
                const name = doc?.name ?? String(id);
                return (
                  <ListItem key={id}>
                    <ListItemText
                      primary={name}
                      primaryTypographyProps={{ noWrap: true }}
                    />
                  </ListItem>
                );
              })}
            </List>
          </Box>
        </DialogContent>
        <DialogActions sx={{ flexShrink: 0 }}>
          <Button
            onClick={handleDeleteCancel}
            variant='outlined'
            color='inherit'
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant='contained'
            color='primary'
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <ErrorSnackBar message={api_error} onClear={() => setApiError(null)} />
    </>
  );
}

export default AdminDocumentList;
